import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import { ActionIcon, Box, Group, Modal } from "@mantine/core";
import { IconMaximize, IconTrash, IconX } from "@tabler/icons-react";
import type { LexicalEditor, NodeKey, NodeSelection, RangeSelection } from "lexical";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from "react";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Position } from "./InlineImageNode";

// Simple in-memory image caching for Suspense loading
const imageCache = new Set<string>();

function useSuspenseImage(src: string) {
  if (!imageCache.has(src)) {
    throw new Promise((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imageCache.add(src);
        resolve(null);
      };
    });
  }
}

function LazyImage({
  altText,
  className,
  src,
  position,
}: {
  altText: string;
  className: string | null;
  src: string;
  position: Position;
}): JSX.Element {
  useSuspenseImage(src);
  return (
    <img
      className={className || undefined}
      src={src}
      alt={altText}
      data-position={position}
      style={{ maxWidth: "100%", maxHeight: 250, display: "block" }}
      draggable={false}
    />
  );
}

const InlineImageComponent: React.FC<{
  altText: string;
  caption: LexicalEditor | null | undefined; // legacy caption editor instance
  height: "inherit" | number;
  nodeKey: NodeKey;
  showCaption: boolean;
  src: string;
  width: "inherit" | number;
  position: Position;
}> = ({ src, altText, nodeKey, width, height, showCaption, caption, position }) => {
  const [showModal, setShowModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const imageNaturalRef = useRef({ width: 0, height: 0 });
  const panStartRef = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<RangeSelection | NodeSelection | null>(null);
  const activeEditorRef = useRef<LexicalEditor | null>(null);

  const isEditable = editor.isEditable();
  const isFocused = isSelected && isEditable;

  const handleDelete = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      node.remove();
    });
    setSelected(false);
  };

  const onDeleteKey = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        payload.preventDefault();
        handleDelete();
      }
      return false;
    },
    [isSelected],
  );

  const onEnter = useCallback(
    (event: KeyboardEvent) => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (isSelected && $isNodeSelection(latestSelection) && latestSelection.getNodes().length === 1) {
        if (showCaption && caption) {
          $setSelection(null);
          event.preventDefault();
          caption.focus();
          return true;
        }
        if (buttonElem && buttonElem !== document.activeElement) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [isSelected, showCaption, caption],
  );

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      const isCaptionActive = caption != null && activeEditorRef.current === caption;
      if (isCaptionActive || buttonRef.current === event.target) {
        $setSelection(null);
        editor.update(() => {
          setSelected(true);
          editor.getRootElement()?.focus();
        });
        return true;
      }
      return false;
    },
    [caption, editor, setSelected],
  );

  useEffect(() => {
    let mounted = true;
    const unregister = mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        if (mounted) {
          editorState.read(() => {
            const sel = $getSelection();
            // Only store NodeSelection or RangeSelection; ignore null/other selection types
            if (sel == null || $isNodeSelection(sel) || sel.constructor.name === "RangeSelection") {
              setSelection(sel as RangeSelection | NodeSelection | null);
            } else {
              setSelection(null);
            }
          });
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (payload) => {
          const target = payload.target as Node | null;
          if (buttonRef.current != null && target != null && buttonRef.current.contains(target)) {
            return false;
          }

          if (wrapperRef.current != null && target != null && wrapperRef.current.contains(target)) {
            if (payload.shiftKey) {
              setSelected(!isSelected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === wrapperRef.current) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDeleteKey, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDeleteKey, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_LOW),
    );
    return () => {
      mounted = false;
      unregister();
    };
  }, [editor, isSelected, clearSelection, onDeleteKey, onEnter, onEscape, setSelected]);

  const resetViewer = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  const clampPan = useCallback(
    (nextPan: { x: number; y: number }, nextZoom = zoom) => {
      const viewer = viewerRef.current;
      const natural = imageNaturalRef.current;
      if (!viewer || natural.width === 0 || natural.height === 0) {
        return { x: 0, y: 0 };
      }

      const viewW = viewer.clientWidth;
      const viewH = viewer.clientHeight;
      const fitScale = Math.min(viewW / natural.width, viewH / natural.height);
      const baseW = natural.width * fitScale;
      const baseH = natural.height * fitScale;
      const scaledW = baseW * nextZoom;
      const scaledH = baseH * nextZoom;
      const maxX = Math.max(0, (scaledW - viewW) / 2);
      const maxY = Math.max(0, (scaledH - viewH) / 2);

      return {
        x: Math.min(maxX, Math.max(-maxX, nextPan.x)),
        y: Math.min(maxY, Math.max(-maxY, nextPan.y)),
      };
    },
    [zoom],
  );

  const applyZoom = useCallback(
    (targetZoom: number) => {
      const nextZoom = Math.min(6, Math.max(1, Number(targetZoom.toFixed(2))));
      setZoom(nextZoom);
      setPan((currentPan) => (nextZoom === 1 ? { x: 0, y: 0 } : clampPan(currentPan, nextZoom)));
    },
    [clampPan],
  );

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setIsFullscreen(false);
    resetViewer();
  }, [resetViewer]);

  const handleViewerWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (!isFullscreen) {
        return;
      }
      event.preventDefault();
      const delta = event.deltaY < 0 ? 0.2 : -0.2;
      applyZoom(zoom + delta);
    },
    [applyZoom, isFullscreen, zoom],
  );

  const handlePanStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!isFullscreen || zoom <= 1) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsPanning(true);
      panStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        originX: pan.x,
        originY: pan.y,
      };
    },
    [isFullscreen, pan.x, pan.y, zoom],
  );

  const handlePanMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = panStartRef.current;
      if (!isPanning || start == null) {
        return;
      }
      const nextPan = {
        x: start.originX + (event.clientX - start.x),
        y: start.originY + (event.clientY - start.y),
      };
      setPan(clampPan(nextPan));
    },
    [clampPan, isPanning],
  );

  const handlePanEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!showModal || !isFullscreen) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleModalClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [handleModalClose, isFullscreen, showModal]);

  const openImageModal = useCallback(() => {
    setShowModal(true);
    setIsFullscreen(true);
    resetViewer();
  }, [resetViewer]);

  const handleShowModal = () => {
    if (!isEditable) {
      openImageModal();
    }
  };

  const handleEditableImagePreview = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      openImageModal();
    },
    [openImageModal],
  );

  return (
    <Suspense fallback={null}>
      <div
        ref={wrapperRef}
        onClick={handleShowModal}
        onDoubleClick={isEditable ? handleEditableImagePreview : undefined}
        className={isEditable ? "editor-image-wrapper" : "editor-image-container"}
        style={{ position: "relative", display: "inline-block" }}
      >
        <LazyImage
          className={isFocused ? `focused ${$isNodeSelection(selection) ? "draggable" : ""}` : null}
          src={src}
          altText={altText}
          position={position}
        />
        {isEditable && isFocused && (
          <Group gap={4} style={{ position: "absolute", bottom: 4, right: 4 }}>
            <ActionIcon
              variant="filled"
              color="gray"
              aria-label="Vis bilde"
              onClick={handleEditableImagePreview}
              shadow="md"
              style={{ boxShadow: "0 0 4px rgba(0,0,0,0.3)" }}
            >
              <IconMaximize size={16} />
            </ActionIcon>
            <ActionIcon
              ref={buttonRef}
              variant="danger-filled"
              aria-label="Slett bilde"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              shadow="md"
              style={{ boxShadow: "0 0 4px rgba(0,0,0,0.3)" }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        )}
      </div>
      <Modal
        opened={showModal}
        onClose={handleModalClose}
        size="60vw"
        yOffset={isFullscreen ? 0 : 80}
        withCloseButton={false}
        fullScreen={isFullscreen}
        closeOnEscape={!isFullscreen}
        overlayProps={{ blur: 2 }}
        keepMounted={false}
        zIndex={1200}
        styles={{
          body: {
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            height: isFullscreen ? "calc(100vh - 4rem)" : "auto",
          },
        }}
      >
        <Group justify="flex-end" gap="xs" mb="xs">
          {isFullscreen && (
            <>
              <ActionIcon variant="subtle" color="gray" onClick={() => applyZoom(zoom - 0.2)} aria-label="Zoom ut">
                <i className="fa fa-search-minus" />
              </ActionIcon>
              <ActionIcon variant="subtle" color="gray" onClick={() => applyZoom(zoom + 0.2)} aria-label="Zoom inn">
                <i className="fa fa-search-plus" />
              </ActionIcon>
              <ActionIcon variant="subtle" color="gray" onClick={resetViewer} aria-label="Nullstill zoom">
                <i className="fa fa-refresh" />
              </ActionIcon>
            </>
          )}
          <ActionIcon variant="subtle" color="gray" onClick={handleModalClose} aria-label="Lukk bildevisning">
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <Box
          ref={viewerRef}
          className="gray-100"
          style={{
            borderRadius: 6,
            minHeight: 0,
            overflow: "hidden",
            flex: isFullscreen ? 1 : undefined,
            height: isFullscreen ? "100%" : "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isFullscreen && zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
            touchAction: "none",
          }}
          onWheel={handleViewerWheel}
          onPointerDown={handlePanStart}
          onPointerMove={handlePanMove}
          onPointerUp={handlePanEnd}
          onPointerCancel={handlePanEnd}
        >
          <img
            src={src}
            alt={altText}
            draggable={false}
            onLoad={(event) => {
              imageNaturalRef.current = {
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              };
              setPan((currentPan) => clampPan(currentPan, zoom));
            }}
            style={{
              width: isFullscreen ? "auto" : "100%",
              maxWidth: "100%",
              maxHeight: isFullscreen ? "100%" : "80vh",
              height: "auto",
              transform: isFullscreen ? `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` : undefined,
              transformOrigin: "center center",
              transition: isPanning ? "none" : "transform 120ms ease-out",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Modal>
    </Suspense>
  );
};

export default InlineImageComponent;
